import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { keyframes } from "@mui/system";

import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/SaveAs";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AddIcon from "@mui/icons-material/Add";
import { trackLesson, BackButton, escapeLatexInJson } from "./functions";
import AutorenewIcon from "@mui/icons-material/Autorenew"; // Magic regenerate icon
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"; // För Försvåra
import LoadingDialog from "./LoadingDialog";
import ShuffleIcon from "@mui/icons-material/Shuffle";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"; // För Förenkla
import { MathJaxContext, MathJax } from "better-react-mathjax";
import Logger from "./Logger";
import { callApi } from "./api"; // Import callApi to interact with AI

const QuestionDisplay = ({
  question,
  index,
  type,
  onUpdateQuestion,
  onDeleteQuestion,
  styles,
  className,
  isAnimating,
  user, // Add user to pass to API
  selectedLanguage = "svenska", // Default language
  selectedModel = "Studera.AI", // Default model
  showSnackbar,
  questionType,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(question.question === "");
  const [isHovered, setIsHovered] = useState(false);
  const [editedQuestion, setEditedQuestion] = useState({
    ...question,
    hints: question.hints || [],
  });

  // States for regeneration and difficulty adjustment
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regeneratePrompt, setRegeneratePrompt] = useState("");
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isDifficultyAdjusting, setIsDifficultyAdjusting] = useState(false);

  const getLetterForIndex = (idx) => String.fromCharCode(65 + idx);
  // Add this useEffect right after your existing useState declarations
  useEffect(() => {
    setEditedQuestion({
      ...question,
      hints: question.hints || [],
    });
  }, [question]);
  const handleShuffleOptions = () => {
    if (!question.options || question.options.length < 2) return;

    // Find current position of correct answer
    const currentCorrectIndex = question.options.findIndex(
      (option) => option === question.correct,
    );
    if (currentCorrectIndex === -1) return;

    const shuffledOptions = [...question.options];

    // Fisher-Yates shuffle
    for (let i = shuffledOptions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledOptions[i], shuffledOptions[j]] = [
        shuffledOptions[j],
        shuffledOptions[i],
      ];
    }

    // Find new position of correct answer
    const newCorrectIndex = shuffledOptions.findIndex(
      (option) => option === question.correct,
    );

    // If correct answer is in same position, swap it with another position
    if (newCorrectIndex === currentCorrectIndex) {
      const swapIndex = (currentCorrectIndex + 1) % shuffledOptions.length;
      [shuffledOptions[currentCorrectIndex], shuffledOptions[swapIndex]] = [
        shuffledOptions[swapIndex],
        shuffledOptions[currentCorrectIndex],
      ];
    }

    const updatedQuestion = {
      ...question,
      options: shuffledOptions,
      correct: question.correct,
    };

    onUpdateQuestion(index, updatedQuestion);
  };

  const handleSave = () => {
    onUpdateQuestion(index, editedQuestion);
    setIsEditing(false);
  };

  // Function to handle opening the regenerate dialog
  const handleOpenRegenerateDialog = () => {
    setRegenerateDialogOpen(true);
  };

  // Function to handle closing the regenerate dialog
  const handleCloseRegenerateDialog = () => {
    setRegenerateDialogOpen(false);
    setRegeneratePrompt("");
  };

  // Function to handle adjusting question difficulty (make easier or harder)
  const handleAdjustDifficulty = async (adjustment) => {
    setIsDifficultyAdjusting(true);

    try {
      // Prepare the message for the API with a specific instruction based on adjustment
      const instruction =
        adjustment === "increase"
          ? "Försvåra denna frågas komplexitetsgrad betydligt. Gör den mer utmanande men fortfarande relevant för ämnet."
          : "Förenkla denna frågas komplexitetsgrad. Bibehåll temat på frågan.";

      const messages = [
        {
          role: "system",
          content: `Du är en expert på att justera svårighetsgraden på quizfrågor. Du kommer att få ett JSON-objekt med en fråga och ska modifiera den enligt instruktionerna. Behåll samma JSON-struktur i ditt svar.


Om det är matematik-frågor: När du använder LaTeX-syntax MÅSTE du omsluta VARJE matematiskt uttryck (oavsett om det är enkla operationer eller komplexa ekvationer) med dollartecken på följande sätt:

För uttryck inom textrader: $x \times y$
För fristående uttryck: $$x \times y$$

Använd ALDRIG [], () eller andra tecken runt matematiska uttryck. ALLTID dollartecken.



          `,
        },
        {
          role: "user",
          content: `Här är en quizfråga i JSON-format:\n\n${JSON.stringify(question, null, 2)}\n\n${instruction} Språket ska vara ${selectedLanguage}. Svara endast med det förbättrade JSON-objektet med samma struktur.`,
        },
      ];

      Logger.log("Question to change:  ", JSON.stringify(question, null, 2));

      // Call the API
      const response = await callApi(messages, 2000, true, user.uid);

      // Extract the response content
      const messageContent = response.data.choices[0].message.content.trim();

      // Parse the JSON from the response
      let parsedResponse;
      try {
        // Extract JSON if it's wrapped in code blocks
        let jsonContent = messageContent;
        if (jsonContent.startsWith("```json")) {
          jsonContent = jsonContent.slice(7, -3).trim();
        } else if (jsonContent.startsWith("```")) {
          jsonContent = jsonContent.slice(3, -3).trim();
        }
        jsonContent = escapeLatexInJson(jsonContent);

        parsedResponse = JSON.parse(jsonContent);

        Logger.log(
          "Question after change:  ",
          JSON.stringify(parsedResponse, null, 2),
        );

        await trackLesson(user.uid, "QuizViewAdjustQuestion.js", null, null);

        // Update the question
        onUpdateQuestion(index, parsedResponse);
        setEditedQuestion(parsedResponse);
      } catch (parseError) {
        console.error("Error parsing JSON response:", parseError);
        showSnackbar("Nåt gick fel. Försök igen", "error");
      }
    } catch (error) {
      console.error("Error adjusting question difficulty:", error);
      showSnackbar("Nåt gick fel. Försök igen", "error");
    } finally {
      setIsDifficultyAdjusting(false);
    }
  };

  // Function to handle regenerating the question
  const handleRegenerateQuestion = async () => {
    if (!regeneratePrompt.trim()) {
      return; // Don't proceed if prompt is empty
    }

    setIsRegenerating(true);
    setRegenerateDialogOpen(false);

    try {
      // Prepare the message for the API
      const messages = [
        {
          role: "system",
          content: `You are an expert at improving quiz questions. You will be given a question JSON object and instructions on how to improve it. Modify the question following these instructions, but maintain the same JSON structure. Always respond with valid JSON only.


Om det är matematik-frågor: När du använder LaTeX-syntax MÅSTE du omsluta VARJE matematiskt uttryck (oavsett om det är enkla operationer eller komplexa ekvationer) med dollartecken på följande sätt:

För uttryck inom textrader: $x \times y$
För fristående uttryck: $$x \times y$$

Använd ALDRIG [], () eller andra tecken runt matematiska uttryck. ALLTID dollartecken.


          `,
        },
        {
          role: "user",
          content: `Here is a quiz question in JSON format:\n\n${JSON.stringify(question, null, 2)}\n\nPlease modify this question based on the following instruction: ${regeneratePrompt}. The question should be in ${selectedLanguage}. Randomize which option position (A, B, C, or D) contains the correct answer to avoid patterns where the first option is always correct. Return only the improved JSON object with the same structure.`,
        },
      ];

      // Call the API
      const response = await callApi(messages, 4000, true, user.uid);

      // Extract the response content
      const messageContent = response.data.choices[0].message.content.trim();

      // Parse the JSON from the response
      let parsedResponse;
      try {
        // Extract JSON if it's wrapped in code blocks
        let jsonContent = messageContent;
        if (jsonContent.startsWith("```json")) {
          jsonContent = jsonContent.slice(7, -3).trim();
        } else if (jsonContent.startsWith("```")) {
          jsonContent = jsonContent.slice(3, -3).trim();
        }
        jsonContent = escapeLatexInJson(jsonContent);

        // Fix the escaped % character issue
        jsonContent = jsonContent.replace(/\\%/g, "%");

        // Then parse the fixed JSON
        parsedResponse = JSON.parse(jsonContent);

        // Update the question
        onUpdateQuestion(index, parsedResponse);
        setEditedQuestion(parsedResponse);

        await trackLesson(
          user.uid,
          "QuizViewRegenerateQuestion.js",
          null,
          null,
        );

        // Close the dialog
        handleCloseRegenerateDialog();
      } catch (parseError) {
        console.error("Error parsing JSON response:", parseError);
        showSnackbar("Nåt gick fel. Försök igen", "info");
      }
    } catch (error) {
      console.error("Error regenerating question:", error);
      showSnackbar("Nåt gick fel. Försök igen", "info");
    } finally {
      setIsRegenerating(false);
    }
  };

  const letterBoxStyles = {
    width: 30,
    height: 30,
    bgcolor: "background.paper",
    border: "2px solid",
    borderColor: "text.main",
    color: "text.main",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mr: 2,
    borderRadius: 1,
    fontSize: "0.9rem",
    fontWeight: "bold",
    // "&:hover": {
    //   bgcolor: "text.main",
    //   color: "white",
    // },
  };

  const renderQuestionContent = () => {
    if (isEditing) {
      if (type === "exam") {
        return (
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                color: theme.palette.text.secondary,
                fontSize: "1rem",
                mb: 1,
              }}
            >
              Ledtrådar:
            </Typography>
            {editedQuestion.hints?.map((hint, hintIdx) => (
              <Box
                key={hintIdx}
                sx={{ display: "flex", alignItems: "center", mb: 1 }}
              >
                <TextField
                  fullWidth
                  value={hint}
                  placeholder={`Ledtråd ${hintIdx + 1}`}
                  onChange={(e) => {
                    const newHints = [...(editedQuestion.hints || [])];
                    newHints[hintIdx] = e.target.value;
                    setEditedQuestion({ ...editedQuestion, hints: newHints });
                  }}
                  onBlur={() => onUpdateQuestion(index, editedQuestion)}
                  sx={{ ...styles.editingTextField }}
                />
                <IconButton
                  onClick={() => {
                    const newHints = editedQuestion.hints.filter(
                      (_, idx) => idx !== hintIdx,
                    );
                    setEditedQuestion({ ...editedQuestion, hints: newHints });
                  }}
                  sx={{
                    width: 40,
                    height: 40,
                    ml: 1,
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="print"
              onClick={() => {
                const newHints = [...(editedQuestion.hints || []), ""];
                setEditedQuestion({ ...editedQuestion, hints: newHints });
              }}
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
            >
              Lägg till ledtråd
            </Button>
          </Box>
        );
      }

      return (
        <Box>
          {editedQuestion.options?.map((option, optIdx) => (
            <Box
              key={optIdx}
              sx={{ display: "flex", alignItems: "center", mb: 2 }}
            >
              <Box sx={letterBoxStyles}>{getLetterForIndex(optIdx)}</Box>
              <TextField
                fullWidth
                value={option}
                sx={{ ...styles.editingTextField }}
                onChange={(e) => {
                  const newOptions = [...editedQuestion.options];
                  newOptions[optIdx] = e.target.value;
                  setEditedQuestion({ ...editedQuestion, options: newOptions });
                }}
                onBlur={() => onUpdateQuestion(index, editedQuestion)}
              />
            </Box>
          ))}

          <TextField
            fullWidth
            label="Rätt svar"
            value={editedQuestion.correct}
            onChange={(e) =>
              setEditedQuestion({ ...editedQuestion, correct: e.target.value })
            }
            onBlur={() => onUpdateQuestion(index, editedQuestion)}
            sx={{ ...styles.editingTextField, mt: 2 }}
          />
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                color: theme.palette.text.secondary,
                fontSize: "1rem",
                mb: 1,
              }}
            >
              Ledtrådar:
            </Typography>
            {editedQuestion.hints?.map((hint, hintIdx) => (
              <Box
                key={hintIdx}
                sx={{ display: "flex", alignItems: "center", mb: 1 }}
              >
                <TextField
                  fullWidth
                  value={hint}
                  placeholder={`Ledtråd ${hintIdx + 1}`}
                  onChange={(e) => {
                    const newHints = [...(editedQuestion.hints || [])];
                    newHints[hintIdx] = e.target.value;
                    setEditedQuestion({ ...editedQuestion, hints: newHints });
                  }}
                  onBlur={() => onUpdateQuestion(index, editedQuestion)}
                  sx={{ ...styles.editingTextField }}
                />
                <IconButton
                  onClick={() => {
                    const newHints = editedQuestion.hints.filter(
                      (_, idx) => idx !== hintIdx,
                    );
                    setEditedQuestion({ ...editedQuestion, hints: newHints });
                  }}
                  sx={{
                    width: 40,
                    height: 40,
                    ml: 1,
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="print"
              onClick={() => {
                const newHints = [...(editedQuestion.hints || []), ""];
                setEditedQuestion({ ...editedQuestion, hints: newHints });
              }}
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
            >
              Lägg till ledtråd
            </Button>
          </Box>
          <Button
            variant="purp"
            onClick={handleSave}
            startIcon={<SaveIcon />}
            sx={{ mt: 4 }}
          >
            Spara
          </Button>
        </Box>
      );
    }

    return (
      <Box>
        {question.options?.map((option, optIdx) => {
          const isCorrect = option === question.correct;
          return (
            <Box
              key={optIdx}
              sx={{ display: "flex", alignItems: "center", mb: 2 }}
            >
              <Box
                sx={{
                  ...letterBoxStyles,
                  ...(isCorrect && {
                    bgcolor: "success.main",
                    color: "white",
                    borderColor: "success.main",
                  }),
                }}
              >
                {getLetterForIndex(optIdx)}
              </Box>
              <Typography sx={{ ...(isCorrect && { fontWeight: 600 }) }}>
                <MathJax>{option}</MathJax>
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <MathJaxContext
        config={{
          tex: {
            inlineMath: [
              ["$", "$"],
              ["\\(", "\\)"],
            ],
            displayMath: [
              ["$$", "$$"],
              ["\\[", "\\]"],
            ],
          },
          chtml: {
            scale: 1.1, // Global scaling factor
            fontFamily: "Arial", // Font to use
            matchFontHeight: false, // Match surrounding font height
            displayAlign: "left", // How to align displayed equations
            displayIndent: "0", // Indent for displayed equations
          },
        }}
      >
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            className={className}
            sx={{
              height: "100%",
              backgroundColor: theme.palette.background.paperHover,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "15px",
              padding: "20px",
              position: "relative",
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <CardContent>
              {(isRegenerating ||
                isDifficultyAdjusting ||
                question.isGenerating) && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 250, 0.95) 100%)",
                    backdropFilter: "blur(8px)",
                    zIndex: 10,
                    borderRadius: "15px",
                    boxShadow: "inset 0 0 20px rgba(0, 0, 0, 0.05)",
                    animation: "fadeIn 0.3s ease-out",
                    "@keyframes fadeIn": {
                      "0%": { opacity: 0 },
                      "100%": { opacity: 1 },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      padding: 3,
                      borderRadius: "50%",
                      background: "rgba(255, 255, 255, 0.7)",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <CircularProgress
                      size={70}
                      thickness={4}
                      sx={{
                        color: "primary.main",
                        "& .MuiCircularProgress-circle": {
                          strokeLinecap: "round",
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      mt: 3,
                      fontWeight: 600,
                      textAlign: "center",
                      color: "#333",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                      maxWidth: "80%",
                      letterSpacing: "0.3px",
                      animation: "pulseText 1.5s infinite ease-in-out",
                      "@keyframes pulseText": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.7 },
                      },
                    }}
                  >
                    {isRegenerating
                      ? "Genererar om frågan..."
                      : isDifficultyAdjusting
                        ? "Justerar svårighetsgrad..."
                        : question.isGenerating
                          ? "Genererar ny fråga med AI..."
                          : ""}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1.5,
                      opacity: 0.75,
                      fontWeight: 400,
                      textAlign: "center",
                      maxWidth: "70%",
                    }}
                  >
                    {isRegenerating
                      ? "Ett ögonblick, vi uppdaterar innehållet..."
                      : isDifficultyAdjusting
                        ? "Vi anpassar svårighetsgraden..."
                        : question.isGenerating
                          ? "Ett ögonblick, AI genererar en ny fråga..."
                          : ""}
                  </Typography>
                </Box>
              )}

              {(isHovered || isEditing || isAnimating) && (
                <Box
                  sx={{
                    position: "absolute",
                    right: 10,
                    top: 10,
                    display: "flex",
                    gap: 1,
                  }}
                >
                  {!isEditing && (
                    <>
                      {/* Försvåra button */}
                      {/*
                      <Tooltip title="Låt AI försvåra frågan" placement="top">
                        <IconButton
                          onClick={() => handleAdjustDifficulty("increase")}
                          disabled={isDifficultyAdjusting}
                        >
                          <ArrowUpwardIcon
                            className={isDifficultyAdjusting ? "spinning" : ""}
                          />
                        </IconButton>
                      </Tooltip>


                      <Tooltip title="Låt AI förenkla frågan" placement="top">
                        <IconButton
                          onClick={() => handleAdjustDifficulty("decrease")}
                          disabled={isDifficultyAdjusting}
                        >
                          <ArrowDownwardIcon
                            className={isDifficultyAdjusting ? "spinning" : ""}
                          />
                        </IconButton>
                      </Tooltip>
                      */}
                      {/* Regenerate button */}
                      <Tooltip
                        title="Låt AI generera om frågan"
                        placement="top"
                      >
                        <IconButton onClick={handleOpenRegenerateDialog}>
                          <AutoFixHighIcon
                            sx={{ color: theme.palette.primary.main }}
                          />
                        </IconButton>
                      </Tooltip>
                      {/* NY SHUFFLE BUTTON - lägg till denna */}
                      {questionType !== "exam" && (
                        <Tooltip
                          title="Slumpa om svarsalternativ"
                          placement="top"
                        >
                          <IconButton onClick={handleShuffleOptions}>
                            <ShuffleIcon sx={{ color: "text.secondary" }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                  <Tooltip title="Radera frågan" placement="top">
                    <IconButton onClick={() => onDeleteQuestion(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={isEditing ? "Spara ändringar" : "Redigera frågan"}
                    placement="top"
                  >
                    <IconButton
                      onClick={
                        isEditing ? handleSave : () => setIsEditing(true)
                      }
                    >
                      {isEditing ? <SaveIcon /> : <EditIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  fontSize: "0.9rem",
                }}
              >
                Fråga {index + 1}
              </Typography>

              {isEditing ? (
                <TextField
                  fullWidth
                  value={editedQuestion.question}
                  onChange={(e) =>
                    setEditedQuestion({
                      ...editedQuestion,
                      question: e.target.value,
                    })
                  }
                  onBlur={() => onUpdateQuestion(index, editedQuestion)}
                  sx={{ ...styles.editingTextField, mb: 6, mt: 1 }}
                />
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    mb: 6,
                    mt: 1,
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    color: theme.palette.text.primary,
                  }}
                >
                  <MathJax>{question.question}</MathJax>
                </Typography>
              )}

              {renderQuestionContent()}
            </CardContent>
          </Card>
        </Grid>
      </MathJaxContext>

      {/* Regenerate Dialog */}
      <Dialog
        open={regenerateDialogOpen}
        onClose={handleCloseRegenerateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Generera om frågan med AI
          <IconButton
            aria-label="close"
            onClick={handleCloseRegenerateDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, mt: 2 }}>
            Beskriv hur du vill förbättra eller ändra frågan:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            placeholder="T.ex. 'Gör den mer akademisk', 'Anpassa för gymnasienivå', 'Lägg till matematiska formler'"
            value={regeneratePrompt}
            onChange={(e) => setRegeneratePrompt(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleRegenerateQuestion}
            variant="purp"
            disabled={isRegenerating || !regeneratePrompt.trim()}
            startIcon={
              isRegenerating ? (
                <CircularProgress sx={{ color: "white" }} size={16} />
              ) : (
                <AutoFixHighIcon />
              )
            }
          >
            {isRegenerating ? "Regenererar..." : "Regenerera"}
          </Button>
        </DialogActions>
      </Dialog>
      <LoadingDialog
        open={false}
        onClose={() => {}}
        hideProgressBar={true}
        title="Genererar om frågan. Snart klart..."
        snackbarMessage="Genereringen är snart klar. Vänligen vänta."
        snackbarSeverity="info"
        progressBarHeight="15px"
        progressBarFontSize="0.9rem"
        coffee={false}
      />
    </>
  );
};

export default QuestionDisplay;
